/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/bolmateStatCard.scss';

import React, {useState} from 'react';
import {Icon} from '@iconify/react';
import trendingUp from '@iconify/icons-feather/trending-up';
import trendingDown from '@iconify/icons-feather/trending-down';
import minus from '@iconify/icons-feather/minus';
import swapHorizontal from '@iconify/icons-mdi/swap-horizontal';
import percent from '@iconify/icons-feather/percent';
import classNames from 'classnames';

export type StatTrend = 'up' | 'down' | 'neutral';

export interface StatCardData {
    label: string;
    value: string | number;
    comparison: string;
    percentage: number;
    prevValue: string | number;
    trend: StatTrend;
}

interface Props extends StatCardData {
    alternate?: StatCardData;
}

interface TrendStyle {
    icon: typeof trendingUp;
    arrow: string;
    className: string;
}

const trendConfig: Record<StatTrend, TrendStyle> = {
    up: {icon: trendingUp, arrow: '\u2191', className: 'positive'},
    down: {icon: trendingDown, arrow: '\u2193', className: 'negative'},
    neutral: {icon: minus, arrow: '\u2014', className: 'neutral'}
};

export default function BolmateStatCard(props: Props) {
    const {alternate} = props;
    const [isAlternate, setIsAlternate] = useState(false);
    const [showPercentMode, setShowPercentMode] = useState(false);

    const data = isAlternate && alternate ? alternate : props;
    const {label, value, comparison, percentage, prevValue, trend} = data;
    const effectiveTrend = percentage === 0 ? 'neutral' : trend;
    const config = trendConfig[effectiveTrend];

    return (
        <div className='bolmate-stat-card'>
            <div className='stat-card-header'>
                <span className='stat-card-label' title={label}>
                    {label}
                </span>
                <div className='stat-card-actions'>
                    <div className={classNames('trend-icon-box', config.className)}>
                        <Icon icon={config.icon} width={16} height={16} />
                    </div>
                    <div className='toggle-actions'>
                        {!!alternate && (
                            <button
                                className={classNames('toggle-btn', isAlternate && 'active')}
                                onClick={() => setIsAlternate(!isAlternate)}
                                title='Weergave wisselen'
                            >
                                <Icon icon={swapHorizontal} width={12} height={12} />
                            </button>
                        )}
                        <button
                            className={classNames('toggle-btn', showPercentMode && 'active')}
                            onClick={() => setShowPercentMode(!showPercentMode)}
                            title='Percentage weergave'
                        >
                            <Icon icon={percent} width={10} height={10} />
                        </button>
                    </div>
                </div>
            </div>

            <div className='stat-card-value-row'>
                <span className='stat-card-main-value'>
                    {showPercentMode ? `${config.arrow} ${percentage}%` : value}
                </span>
                <span className={classNames('stat-card-badge', showPercentMode ? 'muted' : config.className)}>
                    {showPercentMode ? value : `${config.arrow} ${percentage}%`}
                </span>
            </div>

            <div className='stat-card-footer'>
                <span className='stat-card-prev'>Prev: {prevValue}</span>
                <span className={classNames('stat-card-comparison', config.className)}>({comparison})</span>
            </div>
        </div>
    );
}
